import React from "react";
import {
    AutoSearch,
    Loader,
    NumericInput,
    Pane,
    SelectInput,
    Tabs,
    ToolbarMenu,
    ToolbarMenuContent,
} from "~/core";
import { LayerAPI } from "@ai360/core";
import { SearchTools, IToolbar } from "@ai360/core";
import { IconType, ToolbarIcon } from "../../assets/toolbar-icon";
import { logFirebaseEvent } from "~/utils/firebase";
import EventIdSearch from "./event-id-search";

export class SearchToolSet extends SearchTools implements IToolbar {
    _logActiveSearchToFirebase = (index: number): void => {
        let eventName = "";
        switch (index) {
            case 0:
                eventName = "search_city_zip";
                break;
            case 1:
                eventName = "search_lat_long";
                break;
            case 2:
                eventName = "search_plss";
                break;
            case 3:
                eventName = "search_non_field";
                break;
            case 4:
                eventName = "search_event_id";
                break;
        }
        eventName && logFirebaseEvent(eventName);
    };

    getToolbar(): JSX.Element {
        const { messages, formatMessage, userGuid } = this.options;
        return (
            <ToolbarMenu
                testId="map-tools-search-menu"
                className="search"
                label={formatMessage(messages.searchLbl)}
                icon={ToolbarIcon(IconType.SEARCH)}
                onToggle={(evt) => {
                    evt["expanded"] && this._logActiveSearchToFirebase(0);
                    this.toggle(evt);
                }}
            >
                <ToolbarMenuContent className="search-tools">
                    <Tabs
                        selected={this.selectedTab}
                        onPreTabSelect={() => this.reset()}
                        onTabSelect={(index) => {
                            this._logActiveSearchToFirebase(index);
                            this.selectTab(index);
                        }}
                        testId="search-tools-tabs"
                    >
                        <Pane
                            label={formatMessage(messages.searchPlaceTabLbl)}
                            testId="search-tools-place-pane"
                        >
                            <div
                                className="search-pane-content search-city"
                                ref={(scd) => (this.searchCityDiv = scd)}
                            ></div>
                        </Pane>
                        <Pane
                            label={formatMessage(messages.searchXYTabLbl)}
                            testId="search-tools-xy-pane"
                        >
                            <div className="search-pane-content">
                                <NumericInput
                                    containerClassNames={["input-left"]}
                                    placeholderText={formatMessage(messages.latitudeInputLbl)}
                                    value={this.y}
                                    allowNegatives={true}
                                    hasError={this.errors.y.hasError}
                                    errorText={this.errors.y.errorText}
                                    onChange={(y) => (this.y = y)}
                                    onKeyDown={(evt) => this._handleXYKeyDown(evt)}
                                    testId="search-tools-latitude-input"
                                />
                                <NumericInput
                                    placeholderText={formatMessage(messages.longitudeInputLbl)}
                                    value={this.x}
                                    allowNegatives={true}
                                    hasError={this.errors.x.hasError}
                                    errorText={this.errors.x.errorText}
                                    onChange={(x) => (this.x = x)}
                                    onKeyDown={(evt) => this._handleXYKeyDown(evt)}
                                    testId="search-tools-longitude-input"
                                />
                            </div>
                        </Pane>
                        <Pane
                            label={formatMessage(messages.searchPLSSTabLbl)}
                            testId="search-tools-plss-pane"
                        >
                            <div className="search-pane-content">
                                {this.isPLSSLoading && <Loader className="search-loader" />}
                                <SelectInput
                                    placeholderText={formatMessage(messages.stateLbl)}
                                    options={this.states}
                                    clearable={false}
                                    value={this.selectedState}
                                    onChange={(value) =>
                                        this._selectPlss(LayerAPI.PLSSTypes.STATE, value)
                                    }
                                    testId="search-tools-state-select"
                                />
                                <SelectInput
                                    placeholderText={formatMessage(messages.townshipLbl)}
                                    options={this.townships}
                                    clearable={false}
                                    value={this.selectedTownship}
                                    disabled={this.townships.length === 0}
                                    onChange={(value) =>
                                        this._selectPlss(LayerAPI.PLSSTypes.TOWNSHIP, value)
                                    }
                                    testId="search-tools-township-select"
                                />
                                <SelectInput
                                    placeholderText={formatMessage(messages.rangeLbl)}
                                    options={this.ranges}
                                    clearable={false}
                                    value={this.selectedRange}
                                    disabled={this.ranges.length === 0}
                                    onChange={(value) =>
                                        this._selectPlss(LayerAPI.PLSSTypes.RANGE, value)
                                    }
                                    testId="search-tools-range-select"
                                />
                                <SelectInput
                                    placeholderText={formatMessage(messages.sectionLbl)}
                                    options={this.sections}
                                    clearable={false}
                                    value={this.selectedSection}
                                    disabled={this.sections.length === 0}
                                    onChange={(value) =>
                                        this._selectPlss(LayerAPI.PLSSTypes.SECTION, value)
                                    }
                                    testId="search-tools-section-select"
                                />
                            </div>
                        </Pane>
                        <Pane
                            label={formatMessage(messages.searchNonFieldLbl)}
                            testId="search-tools-non-field-pane"
                        >
                            {this.selectedTab !== 3 ? null : (
                                <div className="auto-search-pane-content">
                                    {this.isNFFLoading && <Loader className="search-loader" />}
                                    <AutoSearch
                                        itemList={this.nonFieldFeatures}
                                        keyProp={"value"}
                                        nameProp={"label"}
                                        onResetOptions={() => this.onResetNFFOptions()}
                                        onSelection={(search) =>
                                            this._selectedNonFieldFeature(search)
                                        }
                                        placeholderText={formatMessage(
                                            messages.nonFieldFeatureSearchLbl
                                        )}
                                        resetOptions={this.resetNFFOptions}
                                        secondaryPropList={["secondaryPropList"]}
                                        selectedValue={this.selectedNonFieldFeature}
                                    />
                                </div>
                            )}
                        </Pane>
                        <Pane
                            label={formatMessage(messages.searchEventIdLbl)}
                            testId="search-tools-event-id-pane"
                        >
                            <div className="auto-search-pane-content">
                                <EventIdSearch
                                    forceUpdate={this.options.forceUpdate}
                                    nonFieldFeaturesLoading={this.isNFFLoading}
                                    nonFieldFeatureOptions={this.nonFieldFeatures}
                                    getNonFieldFeatureOptions={() =>
                                        this._getNonFieldFeatureSearchOptions()
                                    }
                                    onSelection={(search) => this._searchEventId(search)}
                                    userGuid={userGuid}
                                />
                            </div>
                        </Pane>
                    </Tabs>
                </ToolbarMenuContent>
            </ToolbarMenu>
        );
    }
}
